import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { env } from '../config/env';

let supabaseClient: SupabaseClient | null = null;

async function initializeSupabase() {
  if (supabaseClient) return supabaseClient;

  try {
    supabaseClient = createClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        storage: window.localStorage, // Ensure we use localStorage for PWA persistence
        storageKey: 'nosh-auth-token', // Custom storage key for your app
        debug: false // Set to true for debugging session issues
      }
    });
    
    // Simple session check without timeout interference
    try {
      await supabaseClient.auth.getSession();
    } catch (sessionError) {
      // Don't throw for session errors, just log them
      console.warn('Session check failed, continuing with limited functionality:', sessionError);
    }
    
    if (env.HAS_SUPABASE_CONFIG) {
      // Supabase client initialized
    } else {
      console.log('ℹ️ Supabase client initialized in demo mode');
    }
  } catch (error: any) {
    console.error('❌ Failed to initialize Supabase client:', error);
    
    // For network errors, create a minimal client that won't cause runtime errors
    if (error.message?.includes('ERR_NAME_NOT_RESOLVED') || error.name === 'AbortError') {
      console.warn('⚠️ Network connectivity issues detected. Creating offline-safe client.');
      
      // Create a basic client that won't attempt network requests
      supabaseClient = createClient('https://offline.supabase.co', 'offline-key', {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
        global: {
          fetch: () => Promise.reject(new Error('Offline mode')),
        },
      });
      
      return supabaseClient;
    }
    
    throw error;
  }

  return supabaseClient;
}

// Initialize and export the client
export const supabase = await initializeSupabase();

// Export helper functions
export const hasValidSupabaseConfig = () => env.HAS_SUPABASE_CONFIG;

export const getSupabaseStatus = () => ({
  isConfigured: env.HAS_SUPABASE_CONFIG,
  client: supabaseClient,
  isDemoMode: !env.HAS_SUPABASE_CONFIG,
});